import { CustomModelAutomaticMutationSideEffectConfigs, SideEffectConfig } from "../types";
import { extractRequestFromQueryKey } from "../../../keys";
import { TellescopeModelFromName } from "../../helper-types";
import { updateModelWithOptions } from "../../../utils";

const applyJourneyMembership = (
  enduser: TellescopeModelFromName<"endusers">,
  journeyId: string,
  action: "add" | "remove"
): TellescopeModelFromName<"endusers"> => {
  const { [journeyId]: _removed, ...remaining } = enduser.journeys ?? {};
  return {
    ...enduser,
    journeys: action === "add" ? { ...remaining, [journeyId]: "" } : remaining,
  };
};

const journeyMembershipSideEffects = (
  enduserIds: string[],
  journeyId: string,
  action: "add" | "remove"
): SideEffectConfig<"endusers"> => {
  const affected = new Set(enduserIds);

  return {
    getOne: {
      predicate: ({ queryKey }) => {
        const queryRequest = extractRequestFromQueryKey(queryKey);
        if (typeof queryRequest === "string") return affected.has(queryRequest);
        return typeof queryRequest?.id === "string" ? affected.has(queryRequest.id) : false;
      },
      updater: (oldData) => (oldData ? applyJourneyMembership(oldData, journeyId, action) : oldData),
    },
    getByIds: {
      predicate: ({ queryKey }) => {
        const queryRequest = extractRequestFromQueryKey(queryKey);
        return !!queryRequest?.ids?.some((id) => affected.has(id));
      },
      updater: (oldData) =>
        oldData
          ? {
              ...oldData,
              matches: oldData.matches.map((enduser) =>
                affected.has(enduser.id) ? applyJourneyMembership(enduser, journeyId, action) : enduser
              ),
            }
          : oldData,
    },
    getSome: {
      exact: false,
      updater: (oldData) =>
        oldData
          ? {
              ...oldData,
              pages: oldData.pages.map((page) =>
                page.map((enduser) =>
                  affected.has(enduser.id) ? applyJourneyMembership(enduser, journeyId, action) : enduser
                )
              ),
            }
          : oldData,
    },
  };
};

export const ENDUSERS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"endusers"> = {
  add_to_journey: ({ enduserIds, journeyId }) => journeyMembershipSideEffects(enduserIds, journeyId, "add"),
  remove_from_journey: ({ enduserIds, journeyId }) => journeyMembershipSideEffects(enduserIds, journeyId, "remove"),
  updateOne: (mutationRequest, mutationResponse) => ({
    check_eligibility: {
      exact: false,
      request: {
        id: mutationResponse?.id ?? mutationRequest.id,
      },
      updater: (oldData) => {
        if (!oldData) return oldData;
        if (mutationResponse) return { ...oldData, enduser: mutationResponse };
        const { _options, ...updates } = mutationRequest;
        return { ...oldData, enduser: updateModelWithOptions(oldData.enduser, updates, _options) };
      },
    },
    is_authenticated: {
      exact: false,
      request: {
        id: mutationResponse?.id ?? mutationRequest.id,
      },
      updater: (oldData) => {
        if (!oldData) return oldData;
        if (mutationResponse)
          return {
            ...oldData,
            enduser: mutationResponse,
          };
        const { _options, ...updates } = mutationRequest;
        return {
          ...oldData,
          enduser: updateModelWithOptions(oldData.enduser, updates, _options),
        };
      },
    },
    related_contacts_report: undefined, // Irrelevant to update based on enduser mutation
    load_inbox_data: {
      exact: false,
      predicate: ({ state: { data }, queryKey }) => {
        const isInList = data?.pages.some((page) =>
          page.endusers.some((enduser) => enduser.id === (mutationResponse?.id ?? mutationRequest.id))
        );
        if (isInList) return true;
        const newPhone = mutationResponse?.phone ?? mutationRequest.phone;
        if (!newPhone) return false;
        const request = extractRequestFromQueryKey(queryKey);
        return request?.phoneNumber
          ? request.phoneNumber.replace(/[^0-9]/, "") === newPhone.replace(/[^0-9]/, "")
          : false;
      },
      updater: (oldData, context) => {
        const queryKey = context?.queryKey;
        if (!oldData || !queryKey) return oldData;
        let existingEnduser: TellescopeModelFromName<"endusers"> | undefined = undefined;
        const isInList = oldData.pages.some((page) =>
          page.endusers.some((enduser) => {
            if (enduser.id === (mutationResponse?.id ?? mutationRequest.id)) {
              existingEnduser = enduser;
              return true;
            }
          })
        );

        if (!existingEnduser && !mutationResponse) {
          return oldData;
        }

        const typedExistingEnduser = existingEnduser as TellescopeModelFromName<"endusers"> | undefined;

        const { _options, ...updates } = mutationRequest;
        const updatedUser = typedExistingEnduser
          ? mutationResponse ?? updateModelWithOptions(typedExistingEnduser, updates, _options)
          : mutationResponse!;

        const updatedPhone = updatedUser.phone && updatedUser.phone.replace(/[^0-9]/, "");
        const oldPhone = typedExistingEnduser?.phone && typedExistingEnduser.phone.replace(/[^0-9]/, "");
        const phoneHasChanged = updatedPhone !== oldPhone;

        const request = extractRequestFromQueryKey(queryKey);
        const shouldBeInList = isInList && request?.phoneNumber ? !phoneHasChanged : true;

        if (shouldBeInList) {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              endusers: page.endusers.map((enduser) => (enduser.id === updatedUser.id ? updatedUser : enduser)),
            })),
          };
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            endusers: [...page.endusers].filter((enduser) => enduser.id !== updatedUser.id),
          })),
        };
      },
    },
    get_journeys_report: undefined, // Irrelevant to update based on enduser mutation
    get_report: undefined, // Irrelevant to update based on enduser mutation
    get_engagement_statistics: undefined, // Irrelevant to update based on enduser mutation
    generate_auth_token: undefined, // Irrelevant to update based on enduser mutation
    get_engagement_statistics_by_userId: undefined, // Irrelevant to update based on enduser mutation
  }),
};
